import React, { useMemo, useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  Grid,
  CircularProgress,
  Alert,
  Button,
  Paper,
  Divider
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  BarChart,
  Bar,
  ComposedChart,
  Cell
} from 'recharts';
import { 
  TrendingUp, 
  TrendingDown, 
  TrendingFlat, 
  Analytics,
  Assessment,
  Speed
} from '@mui/icons-material';
import { reportingApi } from '../../services/reportingApi';

interface CurrentVsHistoricalProps {
  siteId: number;
  siteName?: string;
}

interface CurrentVsHistoricalData {
  site_id: number;
  site_name: string;
  current_year: number;
  comparison_type: string;
  generated_at: string;
  data_source: string;
  monthly_comparison: {
    [month: number]: {
      month: number;
      month_name: string;
      current_year_data: {
        kwh_generated: number | null;
        data_source: string | null;
        data_quality: string | null;
        has_data: boolean;
      };
      historical_statistics: {
        average_kwh: number;
        min_kwh: number;
        max_kwh: number;
        q1_kwh: number;
        q3_kwh: number;
        data_points: number;
      };
      performance_vs_historical: {
        difference_kwh: number | null;
        percentage_of_average: number | null;
        performance_rating: string;
        is_above_average: boolean | null;
        is_within_normal_range: boolean | null;
      };
      partial_month_context?: {
        elapsed_calendar_days?: number;
        /** @deprecated prefer elapsed_calendar_days */
        days_elapsed?: number;
        days_in_month: number;
        month_elapsed_fraction?: number;
        historical_prorated_kwh: number | null;
        comparison_basis: string;
      } | null;
    };
  };
  year_to_date_summary: {
    current_year_total: number;
    historical_average_total: number;
    months_with_current_data: number;
    months_with_historical_data: number;
    ytd_vs_historical: {
      difference_kwh: number | null;
      percentage_of_historical: number | null;
      is_above_historical_pace: boolean | null;
    };
  };
  performance_analysis: {
    average_performance_percentage: number;
    best_month_percentage: number;
    worst_month_percentage: number;
    consistent_performance: boolean;
    months_above_average: number;
    months_below_average: number;
    overall_rating: string;
  };
}

const CurrentVsHistoricalChart: React.FC<CurrentVsHistoricalProps> = ({
  siteId,
  siteName = 'Site'
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<CurrentVsHistoricalData | null>(null);
  const [currentYear, setCurrentYear] = useState<number>(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [chartType, setChartType] = useState<'performance' | 'absolute' | 'ranges'>('performance');
  const [showDetails, setShowDetails] = useState(false);
  const [showHistoricalLines, setShowHistoricalLines] = useState(true);

  // Load available years when component mounts
  useEffect(() => {
    const loadAvailableYears = async () => {
      try {
        const response = await reportingApi.getAvailableYears(siteId);
        const years = response.available_years || [];
        setAvailableYears(years);
        
        // Always set current year first, then fallback if no data
        const currentYearValue = new Date().getFullYear();
        setCurrentYear(currentYearValue); // Always default to current year
        
        // Only change if current year has no data AND there are other years available
        if (!years.includes(currentYearValue) && years.length > 0) {
          console.log(`Current year ${currentYearValue} has no data, using most recent: ${years[0]}`);
          setCurrentYear(years[0]);
        }
      } catch (error) {
        console.error('Failed to load available years:', error);
        // Fallback to recent years
        const currentYearValue = new Date().getFullYear();
        const fallbackYears = [currentYearValue, currentYearValue - 1, currentYearValue - 2];
        setAvailableYears(fallbackYears);
      }
    };
    
    loadAvailableYears();
  }, [siteId]);

  const fetchComparisonData = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await reportingApi.getCurrentVsHistoricalOptimized(siteId, currentYear);
      console.log('Current vs Historical response:', response);
      setData(response);
    } catch (err) {
      setError(`Failed to load comparison data: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Load data whenever currentYear or siteId changes, or on mount
  useEffect(() => {
    if (currentYear && siteId) {
      console.log(`Loading data for year ${currentYear} and site ${siteId}`);
      fetchComparisonData();
    }
  }, [currentYear, siteId]);

  // Ensure immediate load on mount with current year
  useEffect(() => {
    if (!currentYear) {
      // Set current year immediately if not set
      setCurrentYear(new Date().getFullYear());
    }
  }, []);

  const chartData = useMemo(() => {
    if (!data) return [];

    const currentDate = new Date();
    const currentMonthNumber = currentDate.getMonth() + 1; // JavaScript months are 0-based
    const isCurrentYear = currentYear === currentDate.getFullYear();

    const months = Object.keys(data.monthly_comparison).map(Number).sort((a, b) => a - b);
    
    // Filter out future months if we're looking at the current year
    const validMonths = isCurrentYear 
      ? months.filter(month => month <= currentMonthNumber)
      : months;

    return validMonths.map(month => {
      const monthData = data.monthly_comparison[month];
      const current = monthData.current_year_data.kwh_generated;
      const historical = monthData.historical_statistics;
      const performance = monthData.performance_vs_historical;
      const partialCtx = monthData.partial_month_context ?? null;

      return {
        month: monthData.month_name,
        monthNumber: month,
        current: current,
        historical_avg: historical.average_kwh,
        historical_min: historical.min_kwh,
        historical_max: historical.max_kwh,
        historical_q1: historical.q1_kwh,
        historical_q3: historical.q3_kwh,
        performance_percentage: performance.percentage_of_average,
        performance_rating: performance.performance_rating,
        is_above_average: performance.is_above_average,
        within_normal_range: performance.is_within_normal_range,
        data_source: monthData.current_year_data.data_source,
        has_data: monthData.current_year_data.has_data,
        partial_month_context: partialCtx
      };
    });
  }, [data, currentYear]);

  const getPerformanceColor = (rating: string) => {
    switch (rating) {
      case 'excellent': return '#4CAF50';
      case 'good': return '#8BC34A';
      case 'fair': return '#FF9800';
      case 'poor': return '#F44336';
      default: return '#9E9E9E';
    }
  };

  const getPerformanceIcon = (rating: string) => {
    switch (rating) {
      case 'excellent': return <TrendingUp style={{ color: '#4CAF50' }} />;
      case 'good': return <TrendingUp style={{ color: '#8BC34A' }} />;
      case 'fair': return <TrendingFlat style={{ color: '#FF9800' }} />;
      case 'poor': return <TrendingDown style={{ color: '#F44336' }} />;
      default: return <Analytics style={{ color: '#9E9E9E' }} />;
    }
  };

  const ytd = data?.year_to_date_summary;
  const performance = data?.performance_analysis;

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
            <Typography variant="body1" sx={{ ml: 2 }}>
              Loading current vs historical comparison...
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        title={
          <Typography variant="h6">
            Current vs Historical Average - {siteName}
          </Typography>
        }
        subheader={`${currentYear} performance compared to historical statistics (from automated analysis)`}
      />
      <CardContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Controls */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Current Year</InputLabel>
              <Select
                value={currentYear}
                onChange={(e) => setCurrentYear(Number(e.target.value))}
                label="Current Year"
              >
                {availableYears.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                    {year === new Date().getFullYear() && ' (Current)'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Chart View</InputLabel>
              <Select
                value={chartType}
                onChange={(e) => setChartType(e.target.value as any)}
                label="Chart View"
              >
                <MenuItem value="performance">Performance vs Average</MenuItem>
                <MenuItem value="absolute">Absolute Values</MenuItem>
                <MenuItem value="ranges">Statistical Ranges</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              variant="outlined"
              onClick={() => setShowDetails(!showDetails)}
              fullWidth
              sx={{ height: '100%' }}
              startIcon={<Assessment />}
            >
              {showDetails ? 'Hide' : 'Show'} Analysis
            </Button>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              variant={showHistoricalLines ? "contained" : "outlined"}
              onClick={() => setShowHistoricalLines(!showHistoricalLines)}
              fullWidth
              sx={{ height: '100%' }}
              startIcon={<Analytics />}
            >
              Historical Stats
            </Button>
          </Grid>
        </Grid>

        {/* Year-to-Date Summary */}
        {ytd && (
          <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: 'background.paper' }}>
            <Typography variant="h6" gutterBottom>
              📊 Year-to-Date Summary
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={6} md={3}>
                <Typography variant="body2" color="text.secondary">Current Year Total</Typography>
                <Typography variant="h6">{ytd.current_year_total.toLocaleString()} kWh</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="body2" color="text.secondary">Historical Average</Typography>
                <Typography variant="h6">{ytd.historical_average_total.toLocaleString()} kWh</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="body2" color="text.secondary">Performance</Typography>
                {ytd.ytd_vs_historical.percentage_of_historical && (
                  <Typography 
                    variant="h6" 
                    color={ytd.ytd_vs_historical.is_above_historical_pace ? 'success.main' : 'warning.main'}
                  >
                    {ytd.ytd_vs_historical.percentage_of_historical.toFixed(1)}%
                  </Typography>
                )}
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="body2" color="text.secondary">Data Months</Typography>
                <Typography variant="h6">
                  {ytd.months_with_current_data} of {ytd.months_with_historical_data}
                </Typography>
              </Grid>
            </Grid>
            {ytd.ytd_vs_historical.difference_kwh && (
              <Alert 
                severity={ytd.ytd_vs_historical.is_above_historical_pace ? 'success' : 'warning'}
                sx={{ mt: 2 }}
                icon={ytd.ytd_vs_historical.is_above_historical_pace ? <TrendingUp /> : <TrendingDown />}
              >
                <Typography variant="body2">
                  <strong>Year-to-Date vs Historical:</strong>{' '}
                  {ytd.ytd_vs_historical.difference_kwh >= 0 ? '+' : ''}
                  {ytd.ytd_vs_historical.difference_kwh.toLocaleString()} kWh{' '}
                  ({ytd.ytd_vs_historical.percentage_of_historical?.toFixed(1)}% of historical pace)
                </Typography>
              </Alert>
            )}
          </Paper>
        )}

        {/* Performance Analysis */}
        {performance && showDetails && (
          <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: 'background.paper' }}>
            <Typography variant="h6" gutterBottom>
              🎯 Performance Analysis
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={6} md={3}>
                <Box display="flex" alignItems="center" gap={1}>
                  {getPerformanceIcon(performance.overall_rating)}
                  <Box>
                    <Typography variant="body2" color="text.secondary">Overall Rating</Typography>
                    <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
                      {performance.overall_rating.replace('_', ' ')}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="body2" color="text.secondary">Average Performance</Typography>
                <Typography variant="h6">
                  {performance.average_performance_percentage.toFixed(1)}%
                </Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="body2" color="text.secondary">Months Above Average</Typography>
                <Typography variant="h6" color="success.main">
                  {performance.months_above_average}
                </Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="body2" color="text.secondary">Months Below Average</Typography>
                <Typography variant="h6" color="warning.main">
                  {performance.months_below_average}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* Chart */}
        {chartData.length > 0 && (
          <Box sx={{ height: 400, mb: 2 }}>
            <ResponsiveContainer width="100%" height="100%">
              {chartType === 'performance' ? (
                <BarChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis 
                    label={{ value: 'Performance %', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip 
                    formatter={(value: number, name: string) => [
                      `${value?.toFixed(1) || 'N/A'}%`,
                      'Performance vs Historical'
                    ]}
                    labelFormatter={(label) => `Month: ${label}`}
                    content={({ active, payload, label }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <Box sx={{ 
                            backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                            border: '1px solid #ccc', 
                            borderRadius: 1, 
                            p: 2,
                            boxShadow: 2
                          }}>
                            <Typography variant="h6" sx={{ mb: 1 }}>{label}</Typography>
                            <Typography>
                              Performance: {data.performance_percentage?.toFixed(1) || 'N/A'}%
                            </Typography>
                            {data.partial_month_context && (
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                Vs prorated baseline (~
                                {(
                                  data.partial_month_context.elapsed_calendar_days ??
                                  data.partial_month_context.days_elapsed ??
                                  0
                                ).toFixed(2)}{' '}
                                d elapsed
                                {data.partial_month_context.month_elapsed_fraction != null &&
                                  `, ${(data.partial_month_context.month_elapsed_fraction * 100).toFixed(1)}% of month`}
                                ):{' '}
                                {data.partial_month_context.historical_prorated_kwh != null
                                  ? `${Number(data.partial_month_context.historical_prorated_kwh).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
                                  : '—'}{' '}
                                kWh expected vs {data.historical_avg?.toLocaleString()} kWh full-month avg.
                              </Typography>
                            )}
                            <Typography>
                              Rating: <Chip 
                                label={data.performance_rating} 
                                size="small" 
                                sx={{ 
                                  bgcolor: getPerformanceColor(data.performance_rating),
                                  color: 'white',
                                  textTransform: 'capitalize'
                                }}
                              />
                            </Typography>
                            <Typography>
                              Current: {data.current?.toLocaleString() || 'No data'} kWh
                            </Typography>
                            <Typography>
                              Historical Avg: {data.historical_avg?.toLocaleString()} kWh
                            </Typography>
                            {data.data_source && (
                              <Typography variant="caption" color="text.secondary">
                                Source: {data.data_source === 'scada' ? 'Real SCADA Data' : 'Historical Record'}
                              </Typography>
                            )}
                          </Box>
                        );
                      }
                      return null;
                    }}
                  />
                  <Legend />
                  <ReferenceLine y={100} stroke="#666" strokeDasharray="3 3" />
                  <Bar 
                    dataKey="performance_percentage" 
                    name="Performance vs Historical %"
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={getPerformanceColor(entry.performance_rating)} />
                    ))}
                  </Bar>
                </BarChart>
              ) : chartType === 'absolute' ? (
                <ComposedChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis 
                    tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
                    label={{ value: 'kWh', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip 
                    formatter={(value: number, name: string) => [
                      `${value?.toLocaleString() || 'N/A'} kWh`,
                      name === 'current' ? `${currentYear} Actual` : 
                      name === 'historical_avg' ? 'Historical Average' :
                      name === 'historical_min' ? 'Historical Minimum' :
                      name === 'historical_max' ? 'Historical Maximum' : name
                    ]}
                    labelFormatter={(label) => `Month: ${label}`}
                  />
                  <Legend />
                  <Bar dataKey="current" fill="#2196F3" name={`${currentYear} Actual`} />
                  {showHistoricalLines && (
                    <>
                      <Line 
                        type="monotone" 
                        dataKey="historical_avg" 
                        stroke="#FF9800" 
                        strokeWidth={3}
                        name="Historical Average"
                        dot={{ fill: '#FF9800', strokeWidth: 2, r: 4 }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="historical_min" 
                        stroke="#E0E0E0" 
                        strokeWidth={2}
                        strokeDasharray="3 3"
                        name="Historical Minimum"
                        dot={{ fill: '#E0E0E0', strokeWidth: 1, r: 3 }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="historical_max" 
                        stroke="#BDBDBD" 
                        strokeWidth={2}
                        strokeDasharray="3 3"
                        name="Historical Maximum"
                        dot={{ fill: '#BDBDBD', strokeWidth: 1, r: 3 }}
                      />
                    </>
                  )}
                </ComposedChart>
              ) : (
                <ComposedChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis 
                    tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
                    label={{ value: 'kWh', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip 
                    formatter={(value: number, name: string, props: any) => [
                      `${value?.toLocaleString() || 'N/A'} kWh`,
                      name === 'historical_min' ? 'Historical Minimum' :
                      name === 'historical_q1' ? 'Q1 (25th Percentile)' :
                      name === 'historical_avg' ? 'Historical Average' :
                      name === 'historical_q3' ? 'Q3 (75th Percentile)' :
                      name === 'historical_max' ? 'Historical Maximum' :
                      name === 'current' ? `${currentYear} Actual` : name
                    ]}
                    labelFormatter={(label) => `Month: ${label}`}
                    content={({ active, payload, label }) => {
                      if (active && payload && payload.length) {
                        return (
                          <Box sx={{ 
                            backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                            border: '1px solid #ccc', 
                            borderRadius: 1, 
                            p: 2,
                            boxShadow: 2
                          }}>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                              {label}
                            </Typography>
                            {payload.map((entry: any, index: number) => (
                              <Typography 
                                key={index}
                                sx={{ 
                                  color: entry.color,
                                  fontWeight: entry.name === 'historical_avg' ? 'bold' : 'normal'
                                }}
                              >
                                {entry.name === 'historical_min' ? '📉 Historical Minimum' :
                                 entry.name === 'historical_q1' ? '📊 Q1 (25th %ile)' :
                                 entry.name === 'historical_avg' ? '📈 Historical Average' :
                                 entry.name === 'historical_q3' ? '📊 Q3 (75th %ile)' :
                                 entry.name === 'historical_max' ? '📈 Historical Maximum' :
                                 entry.name === 'current' ? `⚡ ${currentYear} Actual` : entry.name}: {' '}
                                {entry.value?.toLocaleString() || 'N/A'} kWh
                              </Typography>
                            ))}
                          </Box>
                        );
                      }
                      return null;
                    }}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="historical_min" stroke="#E0E0E0" strokeDasharray="2 2" name="Historical Min" />
                  <Line type="monotone" dataKey="historical_q1" stroke="#BDBDBD" strokeDasharray="5 5" name="Q1 (25th %ile)" />
                  <Line type="monotone" dataKey="historical_avg" stroke="#FF9800" strokeWidth={3} name="Historical Average" />
                  <Line type="monotone" dataKey="historical_q3" stroke="#BDBDBD" strokeDasharray="5 5" name="Q3 (75th %ile)" />
                  <Line type="monotone" dataKey="historical_max" stroke="#E0E0E0" strokeDasharray="2 2" name="Historical Max" />
                  <Bar dataKey="current" fill="#2196F3" name={`${currentYear} Actual`} opacity={0.8} />
                </ComposedChart>
              )}
            </ResponsiveContainer>
          </Box>
        )}

        {/* Data Source Info */}
        {data && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Data Source:</strong> This comparison uses automated historical statistics 
              calculated from your historical data and real-time SCADA records. 
              Generated at: {new Date(data.generated_at).toLocaleString()}
            </Typography>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default CurrentVsHistoricalChart;
